import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow, ToolbarActionDef } from '@inavista/components/grid';
import { UPGeneralFields } from '../../model/user-profile.model';

// Users page (Service and Sales tabs) on @inavista/components. Rows: RIMS Master api/grid/users?app= (the rows of
// odata/UserProfile / dbo.spGetUserProfiles, paged in SQL, the user from the token). Users are shared by RIMS Service and
// RIMS Sales, so the endpoint is in RIMS Master; the layout is saved through the app's INAVISTA_API (RIMS API in Service,
// RIMS Master in Sales). Same columns, labels, links, row actions and permissions as the Syncfusion grids it replaces.

export type UsersTab = 'service' | 'sales';

export interface UsersGridContext {
  masterApiUrl: string;
  tab: UsersTab;
  // The ISC Code column is not offered to KL roles (the old page removed it).
  showIscCode: boolean;
  // app.rolePermissions of the usersGrid category, as the old page read them.
  canAddUser: { visible: boolean; enable: boolean };
  canExport: boolean;
  canResetPersistence: { visible: boolean; enable: boolean };
  orgTemplates: { territory: TemplateRef<unknown>; district: TemplateRef<unknown>; region: TemplateRef<unknown> };
}

const SERVICE_COLUMNS = [
  UPGeneralFields.userId, 'employeeId', UPGeneralFields.name, 'status', 'role', 'mobile', 'email', UPGeneralFields.createdDate,
  'serviceDepartment', 'serviceManager', 'iscCode',
] as string[];
const SALES_COLUMNS = [
  UPGeneralFields.userId, 'employeeId', UPGeneralFields.name, 'role', 'status', 'mobile', 'email', UPGeneralFields.createdDate,
  'territory', 'district', 'region', 'salesManager',
] as string[];

// Endpoint columns the old grids never showed: never in the column picker.
const BACKING_COLUMNS = ['statusColor', 'applicationsList', 'secondaryTerritories', 'secondaryDistricts', 'secondaryRegions'];
const SERVICE_ONLY = ['serviceDepartment', 'serviceManager', 'iscCode'];
const SALES_ONLY = ['territory', 'district', 'region', 'salesManager'];

const LABELS: Record<string, string> = {
  [UPGeneralFields.userId]: UPGeneralFields.userIdDesc, employeeId: 'Employee Id', [UPGeneralFields.name]: UPGeneralFields.nameDesc,
  status: 'Status', role: 'Role', mobile: UPGeneralFields.mobileDesc, email: UPGeneralFields.emailDesc,
  [UPGeneralFields.createdDate]: UPGeneralFields.createdDateDesc, serviceDepartment: 'Service Department',
  serviceManager: 'Manager', salesManager: 'Manager', iscCode: 'ISC Code', territory: 'Primary Territory',
  district: 'Primary District', region: 'Primary Region',
};

const WIDTHS: Record<string, number> = {
  [UPGeneralFields.userId]: 130, employeeId: 160, [UPGeneralFields.name]: 160, status: 140, role: 160, mobile: 150, email: 160,
  [UPGeneralFields.createdDate]: 140, serviceDepartment: 180, serviceManager: 160, salesManager: 160, iscCode: 160,
  territory: 140, district: 140, region: 140,
};

const CODE_COLUMNS = [UPGeneralFields.userId, 'employeeId', 'iscCode', 'status'] as string[];

const ICON = {
  edit: '<path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />',
  reset: '<polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />',
  reports: '<rect x="9" y="2" width="6" height="5" rx="1" /><rect x="2" y="17" width="6" height="5" rx="1" /><rect x="16" y="17" width="6" height="5" rx="1" /><path d="M12 7v5M5 17v-3h14v3" />',
  active: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />',
  inactive: '<circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />',
  add: '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />',
  refresh: '<polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />',
};

const BLACK = '#000000de';
const GREY = '#ccc';
const NO_PERMISSION = 'Your role does not allow this';

// The old Actions column, in its order: Edit; Reset Persistence and Direct Reports with the reset permission (greyed
// when it is visible but not enabled); Mark Active on an inactive user, Mark Inactive on an active one.
export function usersActionIcons(ctx: UsersGridContext): ActionIconDef[] {
  const reset = ctx.canResetPersistence;
  const resetOff = () => !reset.enable;
  return [
    { key: 'edit', label: 'Edit User', icon: ICON.edit, colorFn: () => BLACK },
    ...(reset.visible ? [
      { key: 'resetPersistence', label: 'Reset Persistence', icon: ICON.reset, colorFn: () => (resetOff() ? GREY : BLACK),
        disabledFn: resetOff, labelFn: () => (resetOff() ? `Reset Persistence: ${NO_PERMISSION}` : 'Reset Persistence') },
      { key: 'directReports', label: 'Direct Reports', icon: ICON.reports,
        colorFn: (row: GridRow) => (resetOff() || !row['applicationsList'] ? GREY : BLACK),
        disabledFn: (row: GridRow) => resetOff() || !row['applicationsList'],
        labelFn: () => (resetOff() ? `Direct Reports: ${NO_PERMISSION}` : 'Direct Reports') },
    ] : []),
    { key: 'markActive', label: 'Mark Active', icon: ICON.active,
      colorFn: row => (row['status'] === 'Inactive' ? '#28a745' : GREY),
      disabledFn: row => row['status'] !== 'Inactive',
      labelFn: row => (row['status'] === 'Inactive' ? 'Mark Active' : 'Already active') },
    { key: 'markInactive', label: 'Mark Inactive', icon: ICON.inactive,
      colorFn: row => (row['status'] === 'Active' ? '#dc3545' : GREY),
      disabledFn: row => row['status'] !== 'Active',
      labelFn: row => (row['status'] === 'Active' ? 'Mark Inactive' : 'Already inactive') },
  ];
}

export function usersToolbarActions(ctx: UsersGridContext): ToolbarActionDef[] {
  return [
    { key: 'refresh', label: 'Refresh', icon: ICON.refresh, kind: 'button', position: 'end' },
    ...(ctx.canAddUser.visible
      ? [{ key: 'addUser', label: ctx.canAddUser.enable ? 'Add New User' : `Add New User: ${NO_PERMISSION}`, icon: ICON.add,
           kind: 'button' as const, position: 'start' as const, disabled: !ctx.canAddUser.enable }]
      : []),
  ];
}

export function usersGridConfig(ctx: UsersGridContext): GridConfig {
  const columns = (ctx.tab === 'service' ? SERVICE_COLUMNS : SALES_COLUMNS).filter(c => c !== 'iscCode' || ctx.showIscCode);
  const notOffered = [...BACKING_COLUMNS, ...(ctx.tab === 'service' ? SALES_ONLY : SERVICE_ONLY), ...(ctx.showIscCode ? [] : ['iscCode'])];
  return {
    apiBaseUrl: `${ctx.masterApiUrl}grid/users`,
    queryParams: { app: ctx.tab },
    keyColumn: UPGeneralFields.userId,
    clientSide: false,
    pageSize: 200,
    rowHeight: 26,
    loadingIndicator: 'banner',
    stretchColumns: true,
    wrapHeaders: true,
    enableSearch: true,
    // The toolbar's Refresh is the page's own (it reloads the permissions too, as the old one): not the grid's second one.
    enableRefresh: false,
    enableContextMenuFilter: true,
    enableFitColumns: true,
    enableSavedViews: true,
    enableResetLayout: true,
    enableExport: ctx.canExport,
    defaultColumnOrder: columns,
    hiddenByDefaultColumns: notOffered,
    // The procedure's order: status (Active first), then name.
    defaultSorts: [{ column: 'status', direction: 'asc' }, { column: UPGeneralFields.name, direction: 'asc' }],
    columnLabels: LABELS,
    columnWidths: WIDTHS,
    columnAlign: Object.fromEntries(columns.map(c => [c, {
      header: { horizontal: 'left' as const },
      ...(CODE_COLUMNS.includes(c) ? { horizontal: 'center' as const } : {}),
    }])),
    columnDateFormats: { [UPGeneralFields.createdDate]: 'd MMM, y' },
    columnColors: { status: (_value, row) => String(row['statusColor'] ?? '') },
    cellTemplates: ctx.tab === 'sales'
      ? { ...ctx.orgTemplates }
      : {},
    // User Id and Name open the user's profile.
    hyperlinkColumns: [UPGeneralFields.userId, UPGeneralFields.name],
    // The old checkbox (Excel) filters.
    excelFilterColumns: ['status', 'role', ...(ctx.tab === 'service' ? ['serviceDepartment', 'serviceManager'] : ['salesManager', 'territory', 'district', 'region'])],
    emptyStateMessage: ctx.tab === 'service'
      ? 'No Service users to show. The list shows the users your role and profile give you access to.'
      : 'No Sales users to show. The list shows the users your role and profile give you access to.',
    persistenceKey: ctx.tab === 'service' ? 'rims.users.list-service' : 'rims.users.list-sales',
    persistenceVersion: 1,
  };
}
